import ActivitySuggestion from 'components/habit-pack-library-activities-autocomplete-input/ActivitySuggestion';
import { OVERLAY_Z_INDEX_CLASS } from 'constants/general';
import { ExtendedSuggestion } from 'interfaces/setting.interface';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface HabitNameSuggestionsDropdownProps {
  suggestions: ExtendedSuggestion[];
  anchorRef: React.RefObject<HTMLElement>;
  onSelect: (suggestion: ExtendedSuggestion) => void;
  onDismiss: () => void;
}

const HabitNameSuggestionsDropdown = ({
  suggestions,
  anchorRef,
  onSelect,
  onDismiss
}: HabitNameSuggestionsDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!suggestions?.length) {
      return;
    }

    const handleDismiss = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && dropdownRef.current?.contains(target)) {
        return;
      }
      onDismiss();
    };

    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('mousedown', handleDismiss);

    return () => {
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('mousedown', handleDismiss);
    };
  }, [onDismiss, suggestions.length]);

  if (!suggestions?.length || !anchorRef.current) {
    return null;
  }

  const anchorRect = anchorRef.current.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;

  return createPortal(
    <div
      ref={dropdownRef}
      className={`absolute ${OVERLAY_Z_INDEX_CLASS.POPOVER} overflow-y-auto scrollbar-thin-custom ~p-1/2 rounded-md border border-gray-200 dark:border-secondary-dark-500 bg-white dark:bg-secondary-dark-200 shadow-lg`}
      style={{
        top: anchorRect.bottom + scrollY + 4,
        left: anchorRect.left + scrollX,
        width: anchorRect.width,
        maxHeight: 384
      }}
      role='listbox'>
      {suggestions.map((suggestion) => (
        <button
          type='button'
          key={`${suggestion.where}-${suggestion.name}-${suggestion.duration}`}
          className='w-full text-left'
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelect(suggestion)}>
          <ActivitySuggestion suggestion={suggestion} />
        </button>
      ))}
    </div>,
    document.body
  );
};

export default HabitNameSuggestionsDropdown;
